import { Box, Grid2, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../../hooks/useNavigationGuard";
import {
  createDrugTypeService,
  updateDrugTypeService,
} from "../../../../services/drugTypeManagementService";
import {
  formatDateWithoutTime,
  noOnlySpaces,
  notifyError,
  notifySuccess,
  validDateRegex,
} from "../../../../utilities/helper";
import Loader from "../../../../components/Loader";
import { CreateHandleUnsaveNavigation } from "../../../../utilities/handleUnsaveNavigation";
import SearchableSelect from "../../../../components/SearchableSelect";
import {
  getKitNumberService,
  getOnLoadDataForRandomizationTreatmentErrorService,
  registerRandomizationAndErrorService,
} from "../../../../services/subjectService";
import style from "./RandomizationTreatment.module.css";
import { HandleUnsaveNavigationWithFun } from "../../../../utilities/handleUnsaveNavigationWithFun";
import CustomSwitch from "../../../../components/CustomSwitch/CustomSwitch";

const RandomizationTreatment = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [subjectDetails, setSubjectDetails] = useState({
    payload: null,
    subjectName: "",
    attributes: [],
    visitDescriptionData: [],
    kitNumberData: [],
    randomizationErrorData: [],
  });

  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");
  const role = localStorage.getItem("role");
  const cultureCode = localStorage.getItem("currentUserCultureCode");

  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const handleUnSaveWithFun = HandleUnsaveNavigationWithFun();

  const {
    subjectName,
    attributes,
    kitNumberData,
    visitDescriptionData,
    randomizationErrorData,
    payload,
  } = subjectDetails;

  const randomizationTreatmentCreateSchema = yup.object().shape({
    subjectStatus: yup
      .string()
      .required("Subject Status is required")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Subject Status must not start with a number or special character",
      )
      .test(
        "has-letter",
        "Subject Status must contain at least one alphabet",
        (value) => /[A-Za-z]/.test(value || ""),
      ),
    subjectNumber: yup.string().required("Subject Number is required"),
    visitDescription: yup.string(),
    kitNumber: yup.string(),
    comments: noOnlySpaces(yup, "comments").required("Comments are required"),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(randomizationTreatmentCreateSchema),
    defaultValues: {
      comments: "",
      kitNumber: "",
      subjectNumber: "",
      subjectStatus: "",
      visitDescription: "",
    },
  });

  useNavigationGuard(isDirty);

  const { visitDescription } = watch();

  useEffect(() => {
    if (access_token && id) {
      getOnLoadDataService(id);
    }
  }, [id]);

  useEffect(() => {
    if (id && visitDescription) {
      getKitDataNumber(id, visitDescription);
    }
  }, [id, visitDescription]);

  const getOnLoadDataService = async (id) => {
    try {
      setLoad(true);
      const res = await getOnLoadDataForRandomizationTreatmentErrorService(
        access_token,
        id,
        cultureCode,
      );
      if (res?.data?.message === "Data Extracted!") {
        const data = res?.data?.result?.patientData;
        const visitData = res?.data?.result?.patientVisits;
        const randomizationErrorData =
          data?.newPatientRegisterEnrollmentTreatmentErrorDto
            ?.enrollmentTreatmentErrors;
        reset({
          subjectNumber: data?.patientNumber,
          subjectStatus: data?.patientStatus,
        });
        setSubjectDetails({
          payload: data,
          subjectName: data?.patientNumber || "",
          attributes: data?.attributes || [],
          visitDescriptionData: visitData || [],
          randomizationErrorData: randomizationErrorData || [],
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  };

  const getKitDataNumber = async (id, patientVisitId) => {
    try {
      setLoad(true);
      const res = await getKitNumberService(
        access_token,
        id,
        patientVisitId,
        cultureCode,
      );
      if (
        res?.data?.message === "kits Extracted successfully!" &&
        res?.data?.result &&
        res?.data?.result?.length > 0
      ) {
        const drugKitData = res?.data?.result?.map((item) => ({
          id: item?.id,
          drugKitNumber: item?.drug?.drugKitNumber,
        }));
        setSubjectDetails((prev) => ({
          ...prev,
          kitNumberData: drugKitData,
        }));
      } else {
        setSubjectDetails((prev) => ({
          ...prev,
          kitNumberData: [],
        }));
      }
    } catch (err) {
      console.log("Error: ", err?.message);
    } finally {
      setLoad(false);
    }
  };

  const addRandomizationTreatmentData = async (data) => {
    setLoad(true);
    // console.log("Data184", data, payload);

    const newData = {
      ...payload,
      newPatientRegisterEnrollmentTreatmentErrorDto: {
        ...payload.newPatientRegisterEnrollmentTreatmentErrorDto,
        comments: data?.comments || "",
        drugKitNumber: data?.kitNumber || "",
        patientVisitDrugId: data?.kitNumber || "",
        visitName: data?.visitDescription || "",
        enrollmentTreatmentErrors: randomizationErrorData?.filter(
          (item) => item?.isSelected === true,
        ),
      },
    };

    // console.log("New Data", newData);

    try {
      const res = await registerRandomizationAndErrorService(
        access_token,
        cultureCode,
        newData,
      );
      console.log("Res208", res);
      if (res?.status == 200) {
        notifySuccess("Data Correction is Updated successfully !");
        reset();
        navigate("/update-subject");
      }
    } catch (err) {
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    } finally {
      setLoad(false);
    }
  };

  const handleSwitchChange = (id) => {
    setSubjectDetails((prev) => ({
      ...prev,
      randomizationErrorData: prev.randomizationErrorData?.map((item) =>
        item.id === id ? { ...item, isSelected: !item.isSelected } : item,
      ),
    }));
  };

  console.log("Errors==??", payload);

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) => {
                handleUnSaveWithFun(
                  e,
                  isDirty,
                  () => navigate("/subject"),
                  () => {},
                );
              }}
            >
              Subject Management
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) => {
                handleUnSaveWithFun(
                  e,
                  isDirty,
                  () =>
                    navigate("/update-subject", {
                      state: {
                        id: id,
                      },
                    }),
                  () => {},
                );
              }}
            >
              {subjectName}
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              to="/subject-randomization-treatment"
            >
              Register Randomization Or Treatment Error
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Subject Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addRandomizationTreatmentData)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Subject Number</p>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={true}
                    type={"text"}
                    {...register("subjectNumber")}
                  />
                </div>
                {errors.subjectNumber && (
                  <div className="error-container">
                    {errors.subjectNumber.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}></Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Subject Status</p>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={true}
                    type={"text"}
                    {...register("subjectStatus")}
                  />
                </div>
                {errors.subjectStatus && (
                  <div className="error-container">
                    {errors.subjectStatus.message}
                  </div>
                )}
              </Grid2>

              {attributes && attributes?.length > 0 && (
                <div className={style?.attributeContainer}>
                  <p className="page-heading" style={{ fontSize: "medium" }}>
                    Subject Attributes
                  </p>
                  {attributes && attributes.length > 0 && (
                    <div className={style?.subjectDetailMainContainer}>
                      {attributes?.map((item) => (
                        <div
                          className={style?.subjectDetailOuterContainer}
                          key={item?.keyName}
                        >
                          <span className={style?.subjectDetailName}>
                            {item?.label}
                          </span>
                          <span className={style?.subjectDetailValue}>
                            {validDateRegex.test(item?.value)
                              ? formatDateWithoutTime(item?.value)
                              : item?.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {attributes && attributes?.length === 0 && (
                <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}></Grid2>
              )}

              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Visit Description</p>
                  <SearchableSelect
                    control={control}
                    data={visitDescriptionData}
                    name={"visitDescription"}
                    optionKey="id"
                    optionLabel="visitName"
                  />
                </div>
                {errors.visitDescription && (
                  <div className="error-container">
                    {errors.visitDescription.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">Kit Number</p>
                  <SearchableSelect
                    control={control}
                    data={kitNumberData}
                    name={"kitNumber"}
                    optionKey="id"
                    optionLabel="drugKitNumber"
                  />
                </div>
                {errors.kitNumber && (
                  <div className="error-container">
                    {errors.kitNumber.message}
                  </div>
                )}
              </Grid2>

              <Grid2
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", margin: "0" }}
              >
                <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                  <p
                    className="page-heading"
                    style={{
                      fontSize: "1.5em",
                    }}
                  >
                    Randomization or Treatment Error(s)
                  </p>
                </Grid2>
                <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}></Grid2>
                <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                  {randomizationErrorData &&
                    randomizationErrorData?.length > 0 &&
                    randomizationErrorData?.map((item) => (
                      <div
                        key={item?.id}
                        className={style?.treatmentErrorContainer}
                      >
                        <span>{item?.description}</span>
                        <CustomSwitch
                          isSwitch={item?.isSelected}
                          setSwitch={() => handleSwitchChange(item?.id)}
                        />
                      </div>
                    ))}
                </Grid2>
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Comments
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="medium"
                    type={"text"}
                    {...register("comments")}
                  />
                </div>
                {errors.comments && (
                  <div className="error-container">
                    {errors.comments.message}
                  </div>
                )}
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) =>
                    handleUnSaveWithFun(
                      e,
                      isDirty,
                      () => navigate("/update-subject", { state: { id: id } }),
                      () => {},
                    )
                  }
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                <button type="submit" className="create-btn">
                  Save
                </button>
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default RandomizationTreatment;
